import { PUZZLE_IMAGES } from "../data/puzzleImages.js";
import { STOP_CATEGORIES } from "../data/stopCategories.js";
import { normalizarRespostaStop, sortearLetraStop } from "./stop.js";

export const ROOM_PLAYER_LIMIT = 8;
export const DEFAULT_ROOM_CONFIG = Object.freeze({
  tempoRodada: 60,
  maxRodadas: 3,
  modoTermo: 1,
  modoQuebraCabeca: 3,
  modoImagemQC: "aleatoria",
});

export function generateRoomCode(random = Math.random) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  return Array.from(
    { length: 4 },
    () => alphabet[Math.floor(random() * alphabet.length)],
  ).join("");
}

export function createRoomState({ code, uid, username }) {
  return {
    code,
    hostId: uid,
    status: "lobby",
    gameId: null,
    jogosJogados: {},
    config: { ...DEFAULT_ROOM_CONFIG },
    players: {
      [uid]: {
        id: uid,
        username,
        points: 0,
        isHost: true,
        pontosPorJogo: {},
      },
    },
  };
}

export function buildPuzzleRound(roomData, round, timestamp) {
  const updates = { rodadaAtual: round };
  for (const uid of Object.keys(roomData.players || {})) {
    updates[`players/${uid}/quebraCabecaStatus`] = null;
  }

  if ((roomData.config?.modoImagemQC || "aleatoria") === "escolher") {
    return {
      ...updates,
      status: "escolhendo_imagem_quebra_cabeca",
      imagemQuebraCabeca: null,
      timestampInicioRodada: null,
    };
  }

  const image = PUZZLE_IMAGES[Math.floor(Math.random() * PUZZLE_IMAGES.length)];
  return {
    ...updates,
    status: "jogando_quebra_cabeca",
    imagemQuebraCabeca: image.url,
    timestampInicioRodada: timestamp,
  };
}

export function buildStopRound(roomData, round, timestamp) {
  return {
    status: "jogando_stop",
    rodadaAtual: round,
    stopLetraAtual: sortearLetraStop(roomData.stopLetrasUsadas || []),
    stopParadoPor: null,
    stopRespostas: null,
    stopVotos: null,
    stopResultadoRodada: null,
    timestampInicioRodada: timestamp + 4000,
  };
}

export function calculateStopResults(roomData) {
  const playerIds = Object.keys(roomData.players || {});
  const answers = roomData.stopRespostas || {};
  const votes = roomData.stopVotos || {};
  const letter = roomData.stopLetraAtual || "";
  const result = Object.fromEntries(
    playerIds.map((uid) => [uid, { pontos: 0, detalhes: {} }]),
  );

  for (const category of STOP_CATEGORIES) {
    const evaluated = playerIds.map((uid) => {
      const text = (answers[uid]?.[category.id] || "").trim();
      if (!text) return { uid, text: "", valid: false };

      const categoryVotes = votes[uid]?.[category.id] || {};
      let approvals = 0;
      let rejections = 0;
      for (const voterId of playerIds) {
        if (voterId === uid) continue;
        if (categoryVotes[voterId] === true) approvals += 1;
        if (categoryVotes[voterId] === false) rejections += 1;
      }

      return {
        uid,
        text,
        valid:
          text.charAt(0).toUpperCase() === letter.toUpperCase() &&
          approvals >= rejections,
      };
    });

    const occurrences = {};
    for (const answer of evaluated) {
      if (!answer.valid) continue;
      const normalized = normalizarRespostaStop(answer.text);
      occurrences[normalized] = (occurrences[normalized] || 0) + 1;
    }

    for (const answer of evaluated) {
      const normalized = normalizarRespostaStop(answer.text);
      const repeated = answer.valid && occurrences[normalized] > 1;
      const points = answer.valid ? (repeated ? 5 : 10) : 0;
      result[answer.uid].pontos += points;
      result[answer.uid].detalhes[category.id] = {
        texto: answer.text,
        valida: answer.valid,
        repetida: repeated,
        pontos: points,
      };
    }
  }

  return result;
}

export function buildStopScoreUpdates(roomData, results) {
  const updates = {
    status: "fim_rodada_stop",
    stopResultadoRodada: results,
    stopLetrasUsadas: [
      ...(roomData.stopLetrasUsadas || []),
      roomData.stopLetraAtual || "",
    ],
  };

  for (const [uid, player] of Object.entries(roomData.players || {})) {
    const points = results[uid]?.pontos || 0;
    updates[`players/${uid}/points`] = (player.points || 0) + points;
    updates[`players/${uid}/pontosPorJogo/stop`] =
      (player.pontosPorJogo?.stop || 0) + points;
  }
  return updates;
}

export function buildLobbyCleanup(roomData, gameId) {
  const updates = {
    status: "lobby",
    gameId: null,
    rodadaAtual: null,
    timestampInicioRodada: null,
    [`jogosJogados/${gameId}`]: true,
  };

  if (gameId === "termo") {
    updates.termoPalavras = null;
    for (const uid of Object.keys(roomData.players || {})) {
      updates[`players/${uid}/termoStatus`] = null;
    }
  } else if (gameId === "quebra-cabeca") {
    updates.imagemQuebraCabeca = null;
    for (const uid of Object.keys(roomData.players || {})) {
      updates[`players/${uid}/quebraCabecaStatus`] = null;
    }
  } else if (gameId === "quem-sou-eu") {
    Object.assign(updates, {
      turnoAtual: null,
      ordemJogadores: null,
      chooserId: null,
      personagemNome: null,
      personagemImagem: null,
      personagemAliases: null,
      resultadoUltimaRodada: null,
    });
    for (const uid of Object.keys(roomData.players || {})) {
      updates[`players/${uid}/quemSouEuStatus`] = null;
    }
  } else if (gameId === "stop") {
    Object.assign(updates, {
      stopLetraAtual: null,
      stopLetrasUsadas: null,
      stopParadoPor: null,
      stopRespostas: null,
      stopVotos: null,
      stopResultadoRodada: null,
    });
  }

  return updates;
}
